/**
 * The animation contract.
 *
 * Everything in `src/anim/` speaks in `Pose` values. A clip produces one, the
 * player blends several, the perception layer overrides part of one, and
 * `RosalieRig` is the only thing that ever touches a `THREE.Object3D`.
 *
 * ## Poses are DELTAS, never absolutes
 *
 * Every field is an offset from the bind pose — the transforms the mesh factory
 * baked in. Her ears already hang forward at 0.72 rad and her snout is already
 * pitched up; a clip that wants a flick asks for `+0.15`, not for `0.87`. A pose
 * of all zeros is her standing exactly as `rosalieAsset.create()` built her.
 *
 * This is what lets D-015 hold: the mesh can be reproportioned — and it was,
 * twice, at G1 — without a single clip changing.
 *
 * ## What may drive what (D-002, and it is structural)
 *
 * > Head, ears and snout express **attention**, never **emotion**.
 *
 * The head/snout/ear channels exist to be driven by a *perception target* — see
 * `PerceptionTarget` — and by nothing else. Gait and posture channels may be
 * driven by anything. There is deliberately no `mood`, `fear` or `sadness`
 * parameter anywhere in this module, and adding one is how the projection
 * mechanism dies.
 */

/** One leg, pivoting at the hip. All rotations are deltas in radians. */
export interface LegPose {
  /** Forward/back swing about X. Positive swings the foot forward. */
  swing: number;
  /** Outward splay about Z. Positive pushes the foot away from her centreline. */
  splay: number;
  /** Foot lift in metres, straight up. Rigid legs have no knee, so this is the
   *  entire "pick the foot up" channel — see the note in `clips.ts`. */
  lift: number;
}

export const NEUTRAL_LEG: Readonly<LegPose> = { swing: 0, splay: 0, lift: 0 };

/** A complete rig pose. Every value is a delta from bind. */
export interface Pose {
  /** Whole-body rise and fall, metres. The gait's vertical bounce lives here. */
  bob: number;
  /**
   * Whole-body scale multipliers, 1 = unchanged.
   *
   * **D-002 caps this at 3–5 %.** `RosalieRig` clamps it rather than trusting
   * callers, because "just a little more for this one landing" is exactly how a
   * restrained plush becomes a cartoon, one clip at a time.
   */
  squashX: number;
  squashY: number;
  squashZ: number;
  /** Body pitch about X. Positive tips her nose down. */
  pitch: number;
  /** Body roll about Z. Positive rolls toward her left. Used to bank into turns. */
  roll: number;
  /** Body yaw about Y, on top of the controller's facing. Small — a shoulder lead. */
  yaw: number;

  /**
   * How tightly she is balled up, 0..1. **The roll clip's channel, and only its.**
   *
   * This is deliberately NOT `squashZ`. D-002 caps squash and stretch at 3–5 %
   * because comic squash is the failure mode that turns a plush into a cartoon,
   * and that cap is worth keeping absolutely. But a Kullerschwein tucking into a
   * ball for her signature move is a different thing from a body squashing on
   * impact, and at 5 % she stayed rigid and read as a log rolling rather than an
   * animal curling up.
   *
   * So the tuck gets its own named channel with its own limit (see
   * `rig.ts`), rather than the 5 % clamp being widened. The squash cap stays
   * exactly where it was, and nothing that reaches for extra compression on a
   * landing can find it here. See the D-002 amendment.
   */
  tuck: number;

  legFL: LegPose;
  legFR: LegPose;
  legBL: LegPose;
  legBR: LegPose;

  /** Neck. Attention only (D-002). */
  headPitch: number;
  headYaw: number;
  headRoll: number;
  /** Snout. Attention only. Also the spec §9 contextual interact prompt. */
  snoutPitch: number;
  snoutYaw: number;

  /** Ears, per side. Attention only. Positive flick rotates the tip up and back. */
  earLFlick: number;
  earLSplay: number;
  earRFlick: number;
  earRSplay: number;

  tailPitch: number;
  tailYaw: number;
}

export function neutralPose(): Pose {
  return {
    bob: 0,
    squashX: 1,
    squashY: 1,
    squashZ: 1,
    pitch: 0,
    roll: 0,
    yaw: 0,
    tuck: 0,
    legFL: { ...NEUTRAL_LEG },
    legFR: { ...NEUTRAL_LEG },
    legBL: { ...NEUTRAL_LEG },
    legBR: { ...NEUTRAL_LEG },
    headPitch: 0,
    headYaw: 0,
    headRoll: 0,
    snoutPitch: 0,
    snoutYaw: 0,
    earLFlick: 0,
    earLSplay: 0,
    earRFlick: 0,
    earRSplay: 0,
    tailPitch: 0,
    tailYaw: 0,
  };
}

/**
 * A clip.
 *
 * `sample` writes into `out` rather than returning a pose, because it is called
 * several times per frame — once per blended layer — and this runs at 60 Hz on
 * integrated graphics.
 *
 * Clips are PURE functions of phase. No internal clocks, no state, no randomness
 * that is not seeded through `phase`. That is what makes a pose reproducible in
 * a screenshot test, and it is why idle variation is expressed as a long
 * secondary cycle rather than as a random timer.
 */
export interface Clip {
  readonly id: string;
  /** Seconds for one full cycle at nominal playback rate. */
  readonly duration: number;
  readonly loop: boolean;
  /**
   * @param phase 0..1 for looping clips; 0..1 clamped for one-shots.
   * @param out   written in place; assume it holds garbage.
   */
  sample(phase: number, out: Pose): void;
}

/** Linear blend of two poses into `out`. `t` = 0 gives `a`, 1 gives `b`. */
export function blendPose(a: Pose, b: Pose, t: number, out: Pose): void {
  const m = (x: number, y: number): number => x + (y - x) * t;
  out.bob = m(a.bob, b.bob);
  out.squashX = m(a.squashX, b.squashX);
  out.squashY = m(a.squashY, b.squashY);
  out.squashZ = m(a.squashZ, b.squashZ);
  out.pitch = m(a.pitch, b.pitch);
  out.roll = m(a.roll, b.roll);
  out.yaw = m(a.yaw, b.yaw);
  out.tuck = m(a.tuck, b.tuck);
  blendLeg(a.legFL, b.legFL, t, out.legFL);
  blendLeg(a.legFR, b.legFR, t, out.legFR);
  blendLeg(a.legBL, b.legBL, t, out.legBL);
  blendLeg(a.legBR, b.legBR, t, out.legBR);
  out.headPitch = m(a.headPitch, b.headPitch);
  out.headYaw = m(a.headYaw, b.headYaw);
  out.headRoll = m(a.headRoll, b.headRoll);
  out.snoutPitch = m(a.snoutPitch, b.snoutPitch);
  out.snoutYaw = m(a.snoutYaw, b.snoutYaw);
  out.earLFlick = m(a.earLFlick, b.earLFlick);
  out.earLSplay = m(a.earLSplay, b.earLSplay);
  out.earRFlick = m(a.earRFlick, b.earRFlick);
  out.earRSplay = m(a.earRSplay, b.earRSplay);
  out.tailPitch = m(a.tailPitch, b.tailPitch);
  out.tailYaw = m(a.tailYaw, b.tailYaw);
}

function blendLeg(a: LegPose, b: LegPose, t: number, out: LegPose): void {
  out.swing = a.swing + (b.swing - a.swing) * t;
  out.splay = a.splay + (b.splay - a.splay) * t;
  out.lift = a.lift + (b.lift - a.lift) * t;
}

export function copyPose(from: Pose, out: Pose): void {
  blendPose(from, from, 0, out);
}

/**
 * What Rosalie is attending to, in world space. Drives head, ears and snout —
 * and it is the ONLY thing permitted to (D-002).
 *
 * `null` means nothing has her attention, and the solver returns her to neutral.
 * That is a real state, not a fallback: a pig with nothing to look at looks at
 * nothing in particular.
 */
export interface PerceptionTarget {
  x: number;
  y: number;
  z: number;
  /**
   * What kind of attention this is. Affects how far the snout leads the head and
   * how hard the ears turn — a sound is tracked with ears first, a scent with
   * the snout, a watched entity with the eyes-forward head.
   */
  kind: 'focus' | 'sound' | 'scent' | 'interactable';
  /** 0..1. Below ~0.15 she barely acknowledges it. */
  weight: number;
}

/** Easing shared by the clips. Named so a curve choice is legible in a diff. */
export const ease = {
  /** Zero velocity at both ends. The default for anything that starts and stops. */
  inOut: (t: number): number => t * t * (3 - 2 * t),
  /** Fast out, slow in — anticipation before a movement. */
  out: (t: number): number => 1 - (1 - t) * (1 - t),
  /** Slow out, fast in — a weight dropping. */
  in: (t: number): number => t * t,
  /** One full sine cycle over 0..1. The spine of every gait. */
  cycle: (t: number): number => Math.sin(t * Math.PI * 2),
  /** Cosine cycle, i.e. `cycle` a quarter-period ahead. */
  cycleCos: (t: number): number => Math.cos(t * Math.PI * 2),
};
